<template>
  <el-form ref="formRef" :model="formData" :rules="rules" v-bind="$attrs">
    <template v-for="(item, index) in _formOptions.itemList" :key="item.prop">
      <el-form-item :label="item[0].label" :prop="item[0].prop" v-if="!item[0]?.hidden">
        <template v-for="innerItem in item">
          <slot
            v-if="innerItem.type === 'Slot'"
            :name="innerItem.slot_name"
            :formData="formData"
          ></slot>
          <component
            v-else
            v-model="formData[innerItem.prop]"
            v-on="bindEvents(innerItem, index)"
            :is="getComponent(innerItem.type)"
            :options="innerItem.options"
            filterable
            :clearable="innerItem.attrs.clearable !== false"
            v-bind="innerItem.attrs"
          >
            <!--下拉框选项 -->
            <el-option
              v-if="innerItem.type === 'Select'"
              v-for="option in unref(innerItem.options)"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            >
              {{ option.label }}
            </el-option>
            <!--多选框选项-->
            <el-checkbox
              v-else-if="innerItem.type === 'Checkbox'"
              v-for="option in innerItem.options"
              :value="option.value"
            >
              {{ option.label }}
            </el-checkbox>
            <!--按钮-->
            <template v-if="innerItem.type === 'Button'">
              {{ innerItem.value }}
            </template>
            <!--插槽-->
            <template
              v-for="(value, slotName) in filterSlots(innerItem.slots)"
              #[parseSlotName(slotName)]
            >
              <slot :name="slotName"></slot>
            </template>
          </component>
        </template>
      </el-form-item>
    </template>
  </el-form>
</template>

<script setup>
import {
  getCurrentInstance,
  inject,
  isRef,
  nextTick,
  reactive,
  ref,
  unref,
  watch,
  watchEffect,
} from "vue";
import MyButton from "./MyButton.vue";
import RadioGroup from "@/components/Common/RadioGroup.vue";

const props = defineProps({
  formOptions: {
    type: Object,
    required: true,
    default: () => ({
      // 列表内容
      itemList: [],
      // 校验规则
      rules: {},
      // 公共属性
      commonAttrs: {},
    }),
  },
});
const _formOptions = reactive({});
const instance = getCurrentInstance();
const formRef = ref(null);
const formData = ref({});
const rules = props.formOptions.rules || {};
// 注入祖先组件传递的数据 echoFormData 用于回显
const echoFormData = inject("echoFormData", ref({}));
// 监听回显对象变化 并入到表单数据进行回显
watchEffect(() => {
  if (!echoFormData.value) return;
  Object.assign(formData.value, unref(echoFormData));
  nextTick(() => isRef(echoFormData) && (echoFormData.value = null));
});

const init = () => {
  packItem();
  handleAttrs();
};

// 将对象类型的 item 包装为数组
const packItem = () => {
  _formOptions.itemList = props.formOptions.itemList.map((item) =>
    Array.isArray(item) ? item : [item]
  );
};
// 处理item的attrs
const handleAttrs = () => {
  const { commonAttrs } = props.formOptions;
  _formOptions.itemList.forEach((_item) => {
    _item.forEach((item) => {
      if (!item.attrs) item.attrs = {};
      bindDefaultValue(item);
      if (commonAttrs) bindCommonAttrs(item);
    });
  });

  // 绑定公共属性到item
  function bindCommonAttrs(item) {
    Object.keys(props.formOptions.commonAttrs).forEach((key) => {
      let value = props.formOptions.commonAttrs[key];
      // 如果是引用类型 则进行合并
      if (typeof value === "object") {
        if (!item.attrs[key]) {
          item.attrs[key] = Array.isArray(value) ? [] : {};
        }
        if (Array.isArray(item.attrs[key])) {
          item.attrs[key].push(...value);
        } else {
          Object.assign(value, item.attrs[key]);
          item.attrs[key] = { ...value };
        }
      } else {
        // 如果是基本数据类型 进行判断该属性是否存在 如果存在则不再进行绑定
        if (item.attrs.hasOwnProperty(key)) return;
        item.attrs[key] = value;
        // 绑定只读
        if (key === "readonly" && value === true) {
          if (!item.attrs.class) item.attrs.class = [];
          item.attrs.class.push("form-item-readonly");
        }
      }
    });
  }

  // 绑定默认值
  function bindDefaultValue(item) {
    const { defaultValue, prop } = item;
    if (defaultValue === undefined) return;
    formData.value[prop] = defaultValue;
  }
};
// 包装事件，传递 {formData, formRef, value, index, ...args}
const bindEvents = (item, index) => {
  if (!item.events || typeof item.events !== "object") return {};
  return Object.fromEntries(
    Object.entries(item.events).map(([eventName, fn]) => [
      eventName,
      (...args) =>
        fn(
          formData.value,
          formRef.value,
          formData.value[item.prop],
          index,
          ...args
        ),
    ])
  );
};
// 插槽
// 筛选插槽
const filterSlots = (slotsName) => {
  if (!slotsName) return [];
  let slots = {};
  slotsName.forEach((name) => {
    if (instance.slots[name]) {
      slots[name] = instance.slots[name];
    }
  });
  return slots;
};
// 解析插槽名称 规范:slotName_id
const parseSlotName = (slotName) => {
  return slotName.split("_")[0];
};
// 获取表单项组件类型
const componentTypes = {
  Input: ElInput,
  Select: ElSelect,
  Radio: RadioGroup,
  Checkbox: ElCheckboxGroup,
  Date: ElDatePicker,
  Button: MyButton,
  Time: ElTimePicker,
  Slot: "",
};
const getComponent = (type) => {
  return componentTypes[type] || ElInput;
};
// 清空表单值
const clearData = () => {
  formData.value = {};
};

init();

defineExpose(
  new Proxy(
    {},
    {
      get(target, key) {
        if (key === "formData") {
          return formData.value;
        } else if (key === "clearData") {
          return clearData;
        } else if (key === "formRef") {
          return formRef.value;
        } else {
          return formRef.value?.[key];
        }
      },
      has() {
        return true;
      },
    }
  )
);
</script>

<style lang="scss">
.search-form {
  --el-text-color-regular: #6fcaf7;
  --el-text-color-primary: #6fcaf7;
  --el-text-color-placeholder: #6fcaf7;
  color: #6fcaf7;
  .el-input__wrapper{
    --el-input-bg-color: #124e91;
    --el-input-border-color: transparent;
    --el-input-hover-border-color: transparent;
  }
  .el-select__wrapper{
    --el-border-color: transparent;
    --el-border-color-hover: transparent;
    background-color: #124e91;
  }
}
</style>
<style lang="scss" scoped>
.el-input,.el-select {
  width: 200px;
}
.form-item-readonly {
  pointer-events: none;
}
</style>
